import { api } from '../../../lib/api';
import type { ListQuery, PagedResult } from '../../administration/users/userTypes';
import type {
  CancelInventoryAuditRequest,
  CreateInventoryAuditRequest,
  InventoryAuditDetail,
  InventoryAuditListItem,
  InventoryAuditListQuery,
  RecordInventoryAuditCountRequest,
  StartInventoryAuditRequest,
} from './inventoryAuditTypes';

/**
 * Serializes the base list query.
 *
 * Every facet is written whenever its KEY IS PRESENT, empty string included — an empty value is
 * the reader saying "none of these", which matches no row. A truthiness check would drop it and
 * the server would read the axis as unmentioned, which is the opposite result.
 */
function base(query: ListQuery, params: URLSearchParams): URLSearchParams {
  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);
  if (query.status !== undefined) params.set('status', query.status);

  return params;
}

function auditQuery(query: InventoryAuditListQuery): string {
  const params = base(query, new URLSearchParams());

  if (query.stage !== undefined) params.set('stage', query.stage);
  if (query.facility !== undefined) params.set('facility', query.facility);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

/**
 * Physical stock takes.
 *
 * <b>There is no `update`.</b> A raised audit records a window and a set of stores, and the
 * baseline is captured against the stores it names — so re-pointing them afterwards would leave a
 * sheet counted at one store filed against another. What looks like an edit is either a cancel
 * with a reason or a delete and a fresh audit, and each says which of those actually happened.
 */
export const inventoryAuditsApi = {
  list: (query: InventoryAuditListQuery, signal?: AbortSignal) =>
    api<PagedResult<InventoryAuditListItem>>(`/api/inventory-audits${auditQuery(query)}`, { signal }),

  /**
   * One audit and its whole count sheet.
   *
   * `includeDeleted` is what the panel's Show deleted sets — without it a struck line is
   * returned by no read, and the restore endpoint has no way to be reached.
   */
  get: (id: string, includeDeleted = false, signal?: AbortSignal) =>
    api<InventoryAuditDetail>(
      `/api/inventory-audits/${id}${includeDeleted ? '?includeDeleted=true' : ''}`,
      { signal },
    ),

  create: (body: CreateInventoryAuditRequest) =>
    api<InventoryAuditDetail>('/api/inventory-audits', { method: 'POST', body }),

  /** Capture the baseline and open the count sheet. */
  start: (id: string, body: StartInventoryAuditRequest) =>
    api<InventoryAuditDetail>(`/api/inventory-audits/${id}/start`, { method: 'POST', body }),

  /** Record ONE line's physical count. The sheet is filled in item by item. */
  recordCount: (id: string, lineId: string, body: RecordInventoryAuditCountRequest) =>
    api<InventoryAuditDetail>(`/api/inventory-audits/${id}/lines/${lineId}`, {
      method: 'PUT',
      body,
    }),

  complete: (id: string) =>
    api<InventoryAuditDetail>(`/api/inventory-audits/${id}/complete`, { method: 'POST' }),

  cancel: (id: string, body: CancelInventoryAuditRequest) =>
    api<InventoryAuditDetail>(`/api/inventory-audits/${id}/cancel`, { method: 'POST', body }),

  deleteLine: (id: string, lineId: string) =>
    api<void>(`/api/inventory-audits/${id}/lines/${lineId}`, { method: 'DELETE' }),

  restoreLine: (id: string, lineId: string) =>
    api<void>(`/api/inventory-audits/${id}/lines/${lineId}/restore`, { method: 'POST' }),

  deactivate: (id: string) => api<void>(`/api/inventory-audits/${id}`, { method: 'DELETE' }),

  activate: (id: string) => api<void>(`/api/inventory-audits/${id}/restore`, { method: 'POST' }),
};
